import React, { useState, useEffect, useCallback } from 'react';
import { useAdminGuard } from '../hooks/useAdminGuard';

const REJECTION_REASONS = [
  "Δεν υλοποιήθηκε η εργασία",
  "Φωτογραφίες μη ικανοποιητικές / μη σαφείς",
  "Δεν υπάρχει εμφανής αλλαγή πριν/μετά",
  "Μετρήσεις ενέργειας δεν δικαιολογούν την εργασία",
  "Άλλο (συμπλήρωσε παρακάτω)"
];

function AdminPage({ account, CONTRACT_ADDRESS, MyContractABI, publicClient, walletClient }) {
  const [pendingTasks, setPendingTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");
  const [customReason, setCustomReason] = useState("");

  useAdminGuard(account);

  const fetchPendingTasks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/pending-tasks', {
        method: 'GET',
        headers: { 
          'adminWallet': account,           // ← αυτό είναι κρίσιμο
          'Content-Type': 'application/json'
        }
      });

      if (!res.ok) {
        console.error(`Admin API error: ${res.status} ${res.statusText}`);
        throw new Error(`HTTP ${res.status}`);
      }

      const data = await res.json();
      setPendingTasks(data.tasks || []);
    } catch (err) {
      console.error('Σφάλμα φόρτωσης pending tasks:', err);
      alert("Σφάλμα φόρτωσης tasks: " + err.message);
    } finally {
      setLoading(false);
    }
  }, [account]);

  const handleReview = async (taskId, decision, category) => {
    let reason = "";
    if (decision === "Rejected") {
      reason = selectedReason === "Άλλο (συμπλήρωσε παρακάτω)" 
        ? customReason 
        : selectedReason;
      
      if (!reason) return alert("Παρακαλώ δώσε λόγο απόρριψης");
    }

    try {
      // On-chain review
      const reviewEnum = decision === "Accepted" ? 1 : 2; // Pending=0, Accepted=1, Rejected=2

      const { request } = await publicClient.simulateContract({
        account,
        address: CONTRACT_ADDRESS,
        abi: MyContractABI,
        functionName: 'reviewTask',
        args: [taskId, reviewEnum, reason],
      });

      const hash = await walletClient.writeContract(request);
      await publicClient.waitForTransactionReceipt({ hash });

      // Backend
      await fetch('/api/admin/review', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'adminWallet': account 
        },
        body: JSON.stringify({
          taskId,
          decision,
          reason,
          // adminWallet: account   ← δεν χρειάζεται μέσα στο body
        })
      });

      alert(`Task ${taskId} → ${decision}`);
      fetchPendingTasks(); // refresh
    } catch (err) {
      console.error(err);
      alert("Σφάλμα: " + err.message);
    }
  };

  useEffect(() => {
    if (account) fetchPendingTasks();
  }, [account, fetchPendingTasks]);

  return (
    <div style={{ padding: '20px' }}>
      <h1>🔑 Admin Review Panel</h1>
      <button onClick={fetchPendingTasks} disabled={loading}>
        {loading ? "Φόρτωση..." : "Refresh Tasks"}
      </button>

      <div>
        {pendingTasks.length === 0 ? (
          <p>Δεν υπάρχουν tasks προς έλεγχο.</p>
        ) : (
          pendingTasks.map(task => (
            <div key={task.taskId} style={{ border: '1px solid #ccc', padding: '15px', margin: '15px 0' }}>
              <h3>
                Task {task.taskId} - Category: {task.category}
                <span style={{ color: 'blue' }}> (Completed)</span>
              </h3>
              <p>Φωτογραφίες: {task.photoCount} | Sensors: {task.sensorCount}</p>
              <div style={{ marginTop: '10px' }}>
                  <a 
                      href={`/dataset/${task.folderName}/photos/before.jpg`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                  >
                      Before Photo
                  </a>
                  <br />
                  <a 
                      href={`/dataset/${task.folderName}/photos/after.jpg`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                  >
                      After Photo
                  </a>
              </div>
              {task.duringPhotos && task.duringPhotos.map((filename, i) => (
                  <div key={i}>
                      <a 
                          href={`/dataset/${task.folderName}/photos/${filename}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                      >
                          During {i+1}
                      </a>
                  </div>
              ))}
              <div style={{ marginTop: '10px' }}>
                <button 
                  onClick={() => handleReview(task.taskId, "Accepted", task.category)}
                  style={{ backgroundColor: 'green', color: 'white', marginRight: '10px' }}
                >
                  ✅ Accept
                </button>

                <select 
                  value={selectedReason} 
                  onChange={(e) => setSelectedReason(e.target.value)}
                  style={{ marginRight: '10px' }}
                >
                  <option value="">Επιλέξτε λόγο απόρριψης...</option>
                  {REJECTION_REASONS.map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>

                {selectedReason === "Άλλο (συμπλήρωσε παρακάτω)" && (
                  <input 
                    type="text" 
                    placeholder="Περιέγραψε τον λόγο..." 
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                  />
                )}

                <button 
                  onClick={() => handleReview(task.taskId, "Rejected", task.category)}
                  style={{ backgroundColor: 'red', color: 'white' }}
                  disabled={!selectedReason}
                >
                  ❌ Reject
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default AdminPage;
